"use client";

import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { ArrowUpDown } from "lucide-react";
import { Link } from "react-router-dom";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge"
import { useNavigate } from 'react-router-dom';
import { Eye, Trash } from "lucide-react";

const options = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
};

// Base columns without selection
const baseColumns: ColumnDef<any>[] = [
  {
    accessorKey: "projectName",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0 hover:bg-transparent"
        >
          Project Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isOffline = row.original.status === 'Offline';
      return (
        <div className="flex items-center gap-2">
          <Link
            to={isOffline ? `/offline/projects/edit-project/${row.original.id}` : `/projects/edit-project/${row.original.id}`}
            className="text-blue hover:underline"
          >
            {row.getValue("projectName")}
          </Link>
          {isOffline && (
            <Badge variant="outline" className="bg-gray-100">
              Offline
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("createdAt"));
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const formattedId = `${year}${month}-PT${row.getValue("id")}`;
      return <div className="w-[120px]">{formattedId}</div>;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: "creator",
    accessorFn: (project) => project.creator?.name ?? "",
    sortingFn: "text",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Owner" />
    ),
    cell: ({ row }) => (
      <div className="flex space-x-2">
        <span className="max-w-[500px] truncate font-medium">
          {row.getValue<string>("creator")}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "overallDecision",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Overall Decision" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex space-x-2">
          <span className={`${row.original?.overallDecision === 'pass'
            ? 'text-green-600'
            : row.original?.overallDecision === 'fail'
              ? 'text-red-600'
              : ''
            } font-semibold max-w-[500px] truncate capitalize`}>
            {row.original?.overallDecision || 'pending'}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created At" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <span>
          {new Date(row.getValue("createdAt")).toLocaleString(
            "en-US",
            options as any
          )}
        </span>
      </div>
    ),
    filterFn: (row, id, value) => {
      const rowDate = new Date(row.getValue(id)).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      return value.includes(rowDate);
    },
  },
  // {
  //   accessorKey: "status",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Status" />
  //   ),
  //   cell: ({ row }) => (
  //     <div className="flex items-center">
  //       <Badge
  //         variant={row.original.offlineMetadata?.onlineId ? "outline" : "default"}
  //         className={row.original.offlineMetadata?.onlineId ? "bg-gray-100" : ""}
  //       >
  //         {row.getValue("status")}
  //       </Badge>
  //     </div>
  //   ),
  // },
  {
    id: "actions",
    header: "Action",
    cell: ({ row, table }) => {
      const onMockDelete = table?.options?.meta?.onMockDelete as ((project: any) => void) | undefined;
      const onDeleteProject = table?.options?.meta?.onDeleteProject as ((project: any) => void) | undefined;
      const deleteHandler = onDeleteProject || onMockDelete;
      const canDelete = row.original.canDelete !== false;

      return (
        <DataTableRowActions
          row={row}
          onDelete={deleteHandler}
          canDelete={canDelete}
        />
      );
    },
  },
];

// Selection column definition
export const getSelectionColumn = (): ColumnDef<any> => ({
  id: 'select',
  enableSorting: false,
  enableHiding: false,
  header: ({ table }) => (
    <div className="px-1">
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    </div>
  ),
  cell: ({ row }) => (
    <div className="px-1">
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    </div>
  ),
});

// Export columns with selection for online mode
export const columns = [getSelectionColumn(), ...baseColumns];

// Export columns2 without selection for offline mode
export const columns2 = baseColumns;

// const handleView = (item: any) => {
//   const navigate = useNavigate();
//   navigate(`/distribution-analytics/view-project/${item._id}`);
// };

interface DistributionProject {
  _id: string;
  project_name: string;
  brand_name: string;
  location_name: string;
  application_name: string;
  design_name: string;
  category: string;
  diameter: string;
  p_height: string;
  s_design: string;
  s_length: string;
  s_width: string;
  s_height: string;
  createdAt: string;
  createdBy: string;
  [key: string]: any; // For other properties
}

interface Row {
  original: DistributionProject;
  getValue(key: string): any;
}

interface CellContextRow extends Row {
  table?: {
    options?: {
      meta?: {
        onMockDelete?: (project: DistributionProject) => void;
        onDeleteProject?: (project: DistributionProject) => void;
      };
    };
  };
}

export const distributionAnalyticsColumns = [
  {
    accessorKey: "project_name",
    header: "Project Name",
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("createdAt"));
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const formattedId = `${year}${month}-PT${row.getValue("id")}`;
      return <div className="w-[120px]">{formattedId}</div>;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "brand_name",
    header: "Brand Name",
    cell: ({ row }: { row: Row }) => {
      const brand = row.original.brand_name;
      const location = row.original.location_name;
      const application = row.original.application_name;
      return `${brand}`;
    },
  },
  // {
  //   accessorKey: "primary",
  //   header: "Primary",
  //   cell: ({ row }: { row: Row }) => {
  //     const design = row.original.design_name;
  //     const category = row.original.category;
  //     const diameter = row.original.diameter;
  //     const height = row.original.p_height;
  //     return `${design}, ${category} D:${diameter} H:${height}`;
  //   },
  // },
  // {
  //   accessorKey: "secondary",
  //   header: "Secondary",
  //   cell: ({ row }: { row: Row }) => {
  //     const design = row.original.s_design;
  //     const length = row.original.s_length;
  //     const width = row.original.s_width;
  //     const height = row.original.s_height;
  //     return `${design} L:${length} W:${width} H:${height}`;
  //   },
  // },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created On" />
    ),
    cell: ({ row }: { row: Row }) => {
      return row.original.createdAt?.substr(0, 10) || '';
    },
  },
  {
    accessorKey: "creator",
    header: "Created By",
    cell: ({ row }: { row: Row }) => {
      return row.original.creator?.name || '';
    },
  },
  {
    id: "actions",
    header: "Action",
    cell: ({ row, table }: { row: CellContextRow; table?: CellContextRow['table'] }) => {
      const onMockDelete = table?.options?.meta?.onMockDelete;
      const onDeleteProject = table?.options?.meta?.onDeleteProject;
      const deleteHandler = onDeleteProject || onMockDelete;
      const canDelete = row.original.canDelete !== false;

      return (
        <div className="flex gap-2">
          <Link
            to={`/distribution-analytics/view-project/${row.original.id}`}
            className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
          >
            <Eye className="w-4 h-4" />
            View
          </Link>
          <button
            onClick={() => deleteHandler?.(row.original)}
            disabled={!deleteHandler || !canDelete}
            className="flex disabled:opacity-50 disabled:cursor-not-allowed items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
          >
            <Trash className="w-4 h-4" />
            Delete
          </button>
        </div>
      );
    },
  },
];
